/**
 * react-intl compatibility shim for react-intl v10.
 *
 * react-intl v10 removed the `injectIntl` HOC. However, some third-party
 * packages (e.g. @atlaskit/editor-common) still import it from 'react-intl'.
 * This shim re-exports everything from the real react-intl and adds an
 * `injectIntl` polyfill built on top of `useIntl`, so those packages continue
 * to work without modification.
 *
 * This file is aliased as 'react-intl' in craco.config.js (webpack only).
 * The real react-intl is imported via a relative filesystem path to
 * node_modules/react-intl/index.js, bypassing the webpack alias and avoiding
 * a circular resolution loop.
 */
import React from 'react';
// Use a relative path to the real react-intl package to avoid the circular
// webpack alias (since 'react-intl' itself is aliased to this shim file).
// Webpack resolves relative paths as filesystem paths, bypassing aliases.
import * as realReactIntl from '../../node_modules/react-intl/index.js';
export * from '../../node_modules/react-intl/index.js';

const { useIntl } = realReactIntl;

/**
 * injectIntl polyfill — wraps a component and injects the intl object as a prop.
 * Mirrors the react-intl v7 API surface used by third-party packages.
 */
export function injectIntl(WrappedComponent, options) {
  const opts = options || {};
  const shouldForwardRef = opts.forwardRef || false;

  const WithIntl = React.forwardRef(function (props, ref) {
    const intl = useIntl();
    const mergedProps = Object.assign({}, props, { intl });
    if (shouldForwardRef) {
      mergedProps.forwardedRef = ref;
    }
    return React.createElement(WrappedComponent, mergedProps);
  });

  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithIntl.displayName = 'InjectIntl(' + displayName + ')';
  WithIntl.WrappedComponent = WrappedComponent;

  return WithIntl;
}

// InjectedIntl and InjectedIntlProps were TypeScript types in react-intl v7.
// Not needed at runtime, but exported for any JS that destructures them.
export const InjectedIntl = undefined;
export const InjectedIntlProps = undefined;
